/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { IComponent, msdyn365Commerce, TelemetryEvent } from '@msdyn365-commerce/core';
import { getCartState, ICartActionResult } from '@msdyn365-commerce/global-state';
import { OrgUnitLocation, ProductDimension, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { NotificationsManager } from '@msdyn365-commerce-modules/notifications-core';
import { getPayloadObject, getTelemetryAttributes, IPopupProps, Popup } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useState } from 'react';
import { getPriceForSelectedVariant, PriceForSelectedVariantInput } from '@msdyn365-commerce-modules/retail-actions';

import { PriceComponent, IAddToCartComponentProps } from '@msdyn365-commerce/components';
import { ICustomWindow } from '../../../../common/utilities/helpers';
import { ItemSuccessfullyAddedToCartNotification } from '@msdyn365-commerce/components';
import { GaItem, pushGoogleAnalyticsEvent } from '../../../../common/pushGoogleAnalyticsEvent';

export interface IAddToCartResources {
    goToCartText: string;
    continueShoppingText: string;
    closeNotificationLabel: string;
    headerItemOneText: string;
    headerItemFormatText: string;
    headerMessageText: string;
    freePriceText: string;
    originalPriceText: string;
    currentPriceText: string;
    addedQuantityText: string;
}

interface IAddCartLine {
    product: SimpleProduct;
    count?: number;
    location?: OrgUnitLocation;
    additionalProperties?: object;
    availableQuantity?: number;
    enableStockCheck?: boolean;
    isAddEmailDeliveryItemToCart?: boolean;
    isPriceKeyedIn?: boolean;
    customPrice?: number;
    deliveryMode?: string;
    shouldSkipSiteSettings?: boolean;
}

export declare type ICartActionFailureReason =
    | 'EMPTYINPUT'
    | 'MISSINGDIMENSION'
    | 'OUTOFSTOCK'
    | 'CARTACTIONFAILED'
    | 'INVALIDCUSTOMAMOUNT';
export interface IAddToCartFailureResult {
    failureReason: ICartActionFailureReason;

    stockLeft?: number;
    cartActionResult?: ICartActionResult;
    missingDimensions?: ProductDimension[];
}

/**
 * This setting defines the experience when a product is added to cart. Corresponds to the configuration in Fabrikam.
 */
export enum AddToCartBehavior {
    /**
     * Navigate to cart page.
     */
    goToCart = 'goToCart',

    /**
     * Show mini cart popup.
     */
    showMiniCart = 'showMiniCart',

    /**
     * Show item added to cart popup.
     */
    showModal = 'showModal',

    /**
     * Show item added to cart notification.
     */
    showNotification = 'showNotification',

    /**
     * Do nothing and stay on the page.
     */
    nothing = 'nothing'
}

export interface IAddToCartComponent extends IComponent<IAddToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddToCartComponentProps) => void;
}

const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    openModal: (opened: boolean) => void
): Promise<void> => {
    const cartError = addToCartError(props);
    let productToAdd = props.data!.product;
    if (cartError) {
        propagateError(props, cartError);
        return;
    }

    setDisabled(true);

    if (!(props.getSelectedProduct === undefined)) {
        productToAdd = (await props.getSelectedProduct) || props.data!.product;
    }

    const cartState = await getCartState(props.context.actionContext);
    const productsToAdd: IAddCartLine[] = [];
    productsToAdd.push({
        product: productToAdd,
        count: props.quantity,
        availableQuantity: props.productAvailability?.AvailableQuantity,
        additionalProperties: { orderQuantityLimitsFeatureIsEnabled: props.isOrderQuantityLimitsFeatureEnabled },
        enableStockCheck: props.context.app.config.enableStockCheck,
        isPriceKeyedIn: props.isPriceKeyedIn,
        customPrice: props.customPriceAmount,
        isAddEmailDeliveryItemToCart: props.isAddEmailDeliveryItemToCart
    });

    const customWindow: ICustomWindow = window;
    if (customWindow.__warranty__) {
        const product = customWindow.__warranty__;
        productsToAdd.push({
            product: product,
            count: props.quantity,
            isPriceKeyedIn: props.isPriceKeyedIn,
            customPrice: props.customPriceAmount,
            isAddEmailDeliveryItemToCart: props.isAddEmailDeliveryItemToCart
        });
    }
    await cartState.addProductsToCart(productsToAdd).then(async result => {
        if (result.status === 'SUCCESS') {
            if (productsToAdd.length === 2) {
                const extensionProperties = cartState.cart.ExtensionProperties || [];
                await cartState.updateExtensionProperties({
                    newExtensionProperties: [
                        ...extensionProperties,
                        {
                            Key: 'linked-' + productsToAdd[0].product.RecordId,
                            Value: { StringValue: `${productsToAdd[0].product.RecordId}-${productsToAdd[1].product.RecordId}` }
                        }
                    ]
                });
            }

            process.env.NODE_ENV === 'development' &&
                console.log('pushGoogleAnalyticsEvent', JSON.parse(JSON.stringify(productsToAdd)), result);

            const gaItems: GaItem[] = [];

            for (const p of productsToAdd) {
                const product = p.product;
                const variant = product.Dimensions?.map(d => d.DimensionValue?.Value).join(',');
                const brandFromExt = product?.ExtensionProperties?.find(p => p.Key === 'Brand')?.Value?.StringValue;

                const productPrice = await getPriceForSelectedVariant(
                    new PriceForSelectedVariantInput(product.RecordId, props.context.actionContext.requestContext.channel?.RecordId!),
                    props.context.actionContext
                );

                const discount = (productPrice?.DiscountLines ?? []).reduce((a, b) => a + (b.Amount ?? 0), 0);

                gaItems.push({
                    recId: product.RecordId,
                    item_id: product.ProductNumber || product.ItemId || '',
                    item_name: product.Name ?? '',
                    index: gaItems.length,
                    item_variant: variant,
                    price: product.Price - discount,
                    discount: discount,
                    pid: product.ItemId || '',
                    item_brand: brandFromExt ?? undefined,
                    quantity: p.count ?? 0
                });
            }

            const totalValue = gaItems.reduce((a, b) => a + (b.price ?? 0) * b.quantity, 0);

            await pushGoogleAnalyticsEvent(
                {
                    event: 'add_to_cart',
                    ecommerce: {
                        currency: 'CAD',
                        value: totalValue,
                        items: gaItems
                    }
                },
                props.context.actionContext
            );

            if (props.dialogStrings && props.context.app.config.addToCartBehavior === AddToCartBehavior.showModal) {
                setDisabled(false);
                openModal(true);
            } else if (props.dialogStrings && props.context.app.config.addToCartBehavior === AddToCartBehavior.showNotification) {
                setDisabled(false);
                const notification = new ItemSuccessfullyAddedToCartNotification(
                    props.context,
                    props.dialogStrings,
                    props.imageSettings,
                    props.gridSettings,
                    props.data!.product,
                    props.data!.price,
                    productsToAdd.length === 2 ? (props.quantity || 1) + 1 : props.quantity || 1,
                    props.navigationUrl,
                    props.telemetryContent!,
                    props.id,
                    props.typeName
                );
                NotificationsManager.instance().addNotification(notification);
            } else if (
                MsDyn365.isBrowser &&
                props.navigationUrl &&
                !props.isNavigationToCartPageDisabled &&
                (props.context.app.config.addToCartBehavior === undefined ||
                    props.context.app.config.addToCartBehavior === AddToCartBehavior.goToCart)
            ) {
                window.location.assign(props.navigationUrl);
            } else {
                setDisabled(false);
            }
            propagateResult(props, result);
        } else {
            propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: result });
            setDisabled(false);
        }
    });
};

const AddToCartComponentActions = {
    onClick
};

const AddToCart: React.FC<IAddToCartComponentProps> = (props: IAddToCartComponentProps) => {
    const [disabled, setDisabled] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        await AddToCartComponentActions.onClick(event, props, setDisabled, setModalOpen);
    };

    const priceComponent = props.data!.price ? (
        <PriceComponent
            data={{ price: props.data!.price }}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            freePriceText={props.dialogStrings?.freePriceText}
            originalPriceText={props.dialogStrings?.originalPriceText}
            currentPriceText={props.dialogStrings?.currentPriceText}
        />
    ) : (
        ''
    );

    const popupProps: IPopupProps = {
        context: props.context,
        className: 'msc-add-to-cart',
        id: props.id,
        typeName: props.typeName,
        data: { product: props.data!.product, price: props.data!.price },
        dialogStrings: props.dialogStrings,
        imageSettings: props.imageSettings,
        gridSettings: props.context.request.gridSettings,
        productQuantity: props.quantity !== undefined ? props.quantity : 1,
        priceComponent,
        navigationUrl: props.navigationUrl,
        modalOpen,
        setModalOpen,
        telemetryContent: props.telemetryContent
    };

    const renderModalPopup = <Popup {...popupProps} />;
    const label = getLinkText(props);
    const payload = getPayloadObject(TelemetryEvent.AddToCart, props.telemetryContent!, label, '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payload);
    return (
        <>
            {renderModalPopup}
            <button
                className={classnames('msc-add-to-cart ', props.className)}
                aria-label={getLinkText(props)}
                {...attributes}
                onClick={onClickHandler}
                disabled={
                    props.disabled ||
                    !props.hasAvailableProducts ||
                    disabled ||
                    isIntermediateState(props) ||
                    shouldShowOutOfStock(props, false)
                }
            >
                {getLinkText(props)}
            </button>
        </>
    );
};

// Set default props
AddToCart.defaultProps = {
    quantity: 1
};

const getLinkText = (props: IAddToCartComponentProps): string => {
    return shouldShowOutOfStock(props, false) ? props.outOfStockText! : props.addToCartText;
};

const addToCartError = (props: IAddToCartComponentProps): IAddToCartFailureResult | undefined => {
    const {
        data,
        productAvailability,
        isCustomPriceSelected,
        customPriceAmount,
        maximumKeyInPrice,
        minimumKeyInPrice,
        defaultMaximumKeyInPrice = 100,
        defaultMinimumKeyInPrice = 10
    } = props;

    if (!data || !data.product.RecordId) {
        // No product exists, won't be able to add to cart
        return { failureReason: 'EMPTYINPUT' };
    }

    if (data.product.Dimensions) {
        const missingDimensions = data.product.Dimensions.filter(
            dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value)
        );

        if (missingDimensions.length > 0) {
            // At least one dimension with no value exists on the product, won't be able to add to cart
            return { failureReason: 'MISSINGDIMENSION', missingDimensions };
        }
    }

    if (shouldShowOutOfStock(props, true)) {
        const availableQuantity = (productAvailability && productAvailability.AvailableQuantity) || 0;
        const stockLeft = Math.max(availableQuantity, 0);

        return { failureReason: 'OUTOFSTOCK', stockLeft };
    }

    // When Custom price is selected, if there is no keyed-in price or keyed-in price is out of limit, should return error.
    if (
        isCustomPriceSelected &&
        (!customPriceAmount ||
            customPriceAmount > (maximumKeyInPrice || defaultMaximumKeyInPrice) ||
            customPriceAmount < (minimumKeyInPrice || defaultMinimumKeyInPrice))
    ) {
        return { failureReason: 'INVALIDCUSTOMAMOUNT' };
    }

    // Only allow adding to cart if not showing out of stock
    return undefined;
};

const shouldShowOutOfStock = (props: IAddToCartComponentProps, includeCurrentQuantity: boolean): boolean => {
    if (
        props.context.app.config.enableStockCheck === undefined ||
        props.context.app.config.enableStockCheck === false ||
        props.isLoading ||
        props.isUpdatingDimension ||
        props.isLoadingDeliveryOptions ||
        props.isUpdatingDeliveryOptions ||
        props.isAddServiceItemToCart
    ) {
        // Out of stock turn off, don't bother showing out of stock
        return false;
    }

    if (!props.data || !props.data!.product.RecordId) {
        // No product exists, don't bother showing out of stock
        return false;
    }

    if (props.data!.product.Dimensions) {
        if (props.data!.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            if (!props.hasAvailableProducts) {
                return true;
            }

            // At least one dimension with no value exists on the product, so also don't show out of stock
            return false;
        }
    }
    const includedQuantityNumber = includeCurrentQuantity && props.quantity ? props.quantity : 1;

    return !(
        props.productAvailability &&
        props.productAvailability.AvailableQuantity !== undefined &&
        props.productAvailability.AvailableQuantity >= includedQuantityNumber
    );
};

const isIntermediateState = (props: IAddToCartComponentProps): boolean => {
    if (props.data!.product.Dimensions) {
        if (props.data!.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also not in intermediate state
            return false;
        }
    }

    if (!props.isLoading && !props.isUpdatingDimension && !props.isLoadingDeliveryOptions && !props.isUpdatingDeliveryOptions) {
        return false;
    }

    return true;
};

const propagateResult = (props: IAddToCartComponentProps, result: ICartActionResult): void => {
    if (props.onAdd) {
        props.onAdd(result);
    }
};

const propagateError = (props: IAddToCartComponentProps, result: IAddToCartFailureResult): void => {
    if (props.onError) {
        props.onError(result);
    }
};

export const AddToCartComponent: React.FunctionComponent<IAddToCartComponentProps> = msdyn365Commerce.createComponentOverride<
    // @ts-expect-error
    IAddToCartComponent
>('AddToCart', { component: AddToCart, ...AddToCartComponentActions });

export default AddToCartComponent;
